import { ColumnDef } from '@tanstack/react-table'
import type {
  MasqueradedEntity,
  MessagesBackofficeMessage,
} from 'api/generated'
import { LongText } from 'components/long-text'
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar'
import { Badge } from 'components/ui/badge'
import moment from 'moment'

// Funzione helper per renderizzare l'utente con avatar, nome e dettagli
const renderParticipant = (participant?: MasqueradedEntity | null) => {
  if (!participant)
    return <span className='text-muted-foreground'>Sconosciuto</span>

  const isFitPlayer = participant.type === 'fit_player_profile'
  // rank/category only exist on fit player profiles (PlayerMasqueraded)
  const rank = 'rank' in participant ? participant.rank : undefined
  const category = 'category' in participant ? participant.category : undefined
  const thumbnail =
    'thumbnail' in participant ? participant.thumbnail : undefined
  // Genera le iniziali per l'AvatarFallback
  const initials = participant.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase()

  return (
    <div className='flex items-center space-x-3'>
      <Avatar className='h-10 w-10 border'>
        <AvatarImage
          src={thumbnail || ''}
          alt={participant.name}
          className='object-cover'
        />
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>

      <div className='flex flex-col'>
        <span
          className='max-w-[150px] truncate text-sm font-semibold'
          title={participant.name}
        >
          {participant.name}
        </span>
        <div className='mt-0.5 flex items-center gap-1.5'>
          <Badge
            variant={isFitPlayer ? 'default' : 'secondary'}
            className='h-4 px-1.5 py-0 text-[10px]'
          >
            {isFitPlayer ? 'FITP' : 'App User'}
          </Badge>

          {/* Mostra Classifica e/o Categoria se disponibili per i profili FITP */}
          {isFitPlayer && (rank || category) && (
            <span className='text-xs font-medium whitespace-nowrap text-muted-foreground'>
              {rank ? `Class. ${rank}` : ''}
              {rank && category ? ' • ' : ''}
              {category}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export const messagesColumns = (): ColumnDef<MessagesBackofficeMessage>[] => [
  {
    accessorKey: 'id',
    header: 'ID',
    cell: ({ getValue }) => (
      <div className='text-center text-xs text-muted-foreground'>
        {String(getValue())}
      </div>
    ),
  },
  {
    accessorKey: 'created_at',
    header: 'Data',
    cell: ({ getValue }) => (
      <div className='text-sm whitespace-nowrap'>
        {moment(getValue() as string).format('DD/MM/YYYY HH:mm')}
      </div>
    ),
  },
  {
    id: 'author',
    header: 'Mittente',
    cell: ({ row }) => renderParticipant(row.original.author),
  },
  {
    id: 'recipient',
    header: 'Destinatario',
    cell: ({ row }) => renderParticipant(row.original.recipient),
  },
  {
    accessorKey: 'content',
    header: 'Messaggio',
    cell: ({ getValue }) => (
      <LongText className='max-w-[250px] text-sm xl:max-w-[400px]'>
        {String(getValue())}
      </LongText>
    ),
  },
  {
    accessorKey: 'is_blocked',
    header: 'Stato',
    cell: ({ row }) =>
      row.original.is_blocked ? (
        <Badge variant='destructive'>Bloccato</Badge>
      ) : (
        <Badge variant='outline' className='border-green-600 text-green-600'>
          Pubblico
        </Badge>
      ),
  },
]
